import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./PublicBookingWidget.css";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarDays, CheckCircle2, Clock, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function extractErrorMessage(data) {
  if (!data) return "Something went wrong. Please try again.";
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail) && data.detail.length) {
    return data.detail.map((d) => d.msg || String(d)).join("; ");
  }
  return "Something went wrong. Please try again.";
}

async function publicFetch(url, { method = "GET", body } = {}) {
  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    // no JSON body - fine
  }
  if (!res.ok) {
    const err = new Error(extractErrorMessage(data));
    err.status = res.status;
    throw err;
  }
  return data;
}

const centsToDisplay = (cents) => `$${(cents / 100).toFixed(2)}`;

// The patient's *local* today. toISOString() is UTC, so in US evenings it
// was already tomorrow - today couldn't be picked (it was below `min`).
const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * Public, unauthenticated self-serve booking widget - a patient books
 * directly at /book/{clientId}, no staff or client-portal login. Mirrors
 * the availability -> pick a slot -> confirm flow already proven in
 * AppointmentsTab.js, but as a standalone page rather than a tab, since
 * this has no app shell, no auth, and a very different audience.
 */
const PublicBookingWidget = ({ clientId }) => {
  const base = `${BACKEND_URL}/api/public/v1/booking/${clientId}`;

  const [step, setStep] = useState("loading"); // loading | unavailable | service | provider | day | slot | details | success | error
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [day, setDay] = useState(todayIso());
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await publicFetch(`${base}/services`);
        if (cancelled) return;
        setServices(data.items || []);
        setStep("service");
      } catch (err) {
        if (cancelled) return;
        setStep(err.status === 404 ? "unavailable" : "error");
        setErrorMessage(err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const loadProviders = useCallback(
    async (service) => {
      try {
        const data = await publicFetch(`${base}/providers?service_id=${encodeURIComponent(service.id)}`);
        setProviders(data.items || []);
        setStep("provider");
      } catch (err) {
        setErrorMessage(err.message);
        setStep("error");
      }
    },
    [base]
  );

  const loadAvailability = useCallback(
    async (provider, service, forDay) => {
      setLoadingSlots(true);
      setSelectedSlot(null);
      try {
        const params = new URLSearchParams({ provider_id: provider.id, service_id: service.id, day: forDay });
        const data = await publicFetch(`${base}/availability?${params.toString()}`);
        setSlots(data.available_starts || []);
      } catch (err) {
        setErrorMessage(err.message);
        setSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    },
    [base]
  );

  const pickService = (service) => {
    setSelectedService(service);
    setSelectedProvider(null);
    loadProviders(service);
  };

  const pickProvider = (provider) => {
    setSelectedProvider(provider);
    setStep("day");
    loadAvailability(provider, selectedService, day);
  };

  const changeDay = (value) => {
    setDay(value);
    if (selectedProvider && selectedService) loadAvailability(selectedProvider, selectedService, value);
  };

  const pickSlot = (iso) => {
    setSelectedSlot(iso);
    setStep("details");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.email && !form.phone) {
      setErrorMessage("Please provide an email or phone number so the clinic can reach you.");
      return;
    }
    setErrorMessage("");
    setSubmitting(true);
    try {
      const result = await publicFetch(`${base}/appointments`, {
        method: "POST",
        body: {
          patient: {
            first_name: form.first_name,
            last_name: form.last_name,
            email: form.email || null,
            phone: form.phone || null,
          },
          provider_id: selectedProvider.id,
          service_id: selectedService.id,
          starts_at: selectedSlot,
          notes: form.notes,
        },
      });
      setConfirmation(result);
      setStep("success");
    } catch (err) {
      if (err.status === 409) {
        setErrorMessage("That time was just booked by someone else. Please pick another slot.");
        setStep("slot");
        loadAvailability(selectedProvider, selectedService, day);
      } else {
        setErrorMessage(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formattedSlot = useMemo(
    () => (selectedSlot ? new Date(selectedSlot).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" }) : ""),
    [selectedSlot]
  );

  if (step === "loading") {
    return (
      <div className="public-booking-shell">
        <p className="public-booking-muted">Loading...</p>
      </div>
    );
  }

  if (step === "unavailable") {
    return (
      <div className="public-booking-shell">
        <Card className="glass-panel border-white/10 public-booking-card">
          <CardContent className="pt-6 text-center py-12">
            <CalendarDays className="h-10 w-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/70">Online booking isn't available for this clinic right now.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="public-booking-shell">
      <Card className="glass-panel border-white/10 public-booking-card" data-testid="public-booking-widget">
        <CardContent className="pt-6 space-y-5">
          <div>
            <p className="eyebrow">Book an appointment</p>
          </div>

          {errorMessage && step !== "details" && <div className="form-error">{errorMessage}</div>}

          {step === "service" && (
            <div className="space-y-2" data-testid="public-booking-step-service">
              <Label className="text-white/70">Choose a service</Label>
              {services.length === 0 ? (
                <p className="public-booking-muted">No services are available to book right now.</p>
              ) : (
                services.map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    className="public-booking-option"
                    data-testid={`public-service-${service.id}`}
                    onClick={() => pickService(service)}
                  >
                    <span className="public-booking-option-title">{service.name}</span>
                    <span className="public-booking-option-meta">
                      {service.duration_minutes} min - {centsToDisplay(service.price_cents)}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}

          {step === "provider" && (
            <div className="space-y-2" data-testid="public-booking-step-provider">
              <Label className="text-white/70">Choose a provider</Label>
              {providers.length === 0 ? (
                <p className="public-booking-muted">No providers are available for this service.</p>
              ) : (
                providers.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    className="public-booking-option"
                    data-testid={`public-provider-${provider.id}`}
                    onClick={() => pickProvider(provider)}
                  >
                    <User size={16} />
                    <span className="public-booking-option-title">{provider.full_name}</span>
                    {provider.title && <span className="public-booking-option-meta">{provider.title}</span>}
                  </button>
                ))
              )}
              <Button className="ghost-btn" onClick={() => setStep("service")}>Back</Button>
            </div>
          )}

          {(step === "day" || step === "slot") && (
            <div className="space-y-3" data-testid="public-booking-step-day">
              <Label className="text-white/70">Choose a day</Label>
              <Input
                type="date"
                min={todayIso()}
                value={day}
                onChange={(e) => changeDay(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
              <Label className="text-white/70 flex items-center gap-2"><Clock size={14} /> Available times</Label>
              {loadingSlots ? (
                <p className="public-booking-muted">Checking availability...</p>
              ) : slots.length === 0 ? (
                <p className="public-booking-muted">No open times that day - try another date.</p>
              ) : (
                <div className="public-booking-slot-grid">
                  {slots.map((iso) => (
                    <button
                      key={iso}
                      type="button"
                      className="public-booking-slot"
                      data-testid={`public-slot-${iso}`}
                      onClick={() => pickSlot(iso)}
                    >
                      {new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                    </button>
                  ))}
                </div>
              )}
              <Button className="ghost-btn" onClick={() => setStep("provider")}>Back</Button>
            </div>
          )}

          {step === "details" && (
            <form onSubmit={submit} className="space-y-3" data-testid="public-booking-step-details">
              <p className="public-booking-muted">{formattedSlot} (your local time)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white/70">First name</Label>
                  <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-white/70">Last name</Label>
                  <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-white/70">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="At least one of email or phone" />
              </div>
              <div>
                <Label className="text-white/70">Notes (optional)</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              {errorMessage && <div className="form-error">{errorMessage}</div>}
              <div className="flex gap-2">
                <Button type="button" className="ghost-btn" onClick={() => setStep("slot")}>Back</Button>
                <Button type="submit" className="primary-btn" disabled={submitting} data-testid="public-booking-confirm-btn">
                  {submitting ? "Booking..." : "Confirm booking"}
                </Button>
              </div>
            </form>
          )}

          {step === "success" && confirmation && (
            <div className="text-center py-8 space-y-3" data-testid="public-booking-success">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
              <p className="text-white/90 font-medium">You're booked!</p>
              <p className="public-booking-muted">{formattedSlot}</p>
            </div>
          )}

          {step === "error" && <div className="form-error">{errorMessage || "Something went wrong. Please try again."}</div>}
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicBookingWidget;
